import React, { useCallback, useState } from 'react';

import styled, { css } from 'styled-components';

import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import Gavel from '@mui/icons-material/Gavel';
import HelpIcon from '@mui/icons-material/Help';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import { LANGUAGE_NAMES } from '@cd3p/core/constants/common';
import { DocumentsSections } from '@cd3p/core/modules/documents';
import { userProviders } from '@cd3p/core/selectors/app';
import { APIAccessLevel, APIUserType } from '@cd3p/core/types/api';
import {
  AnimatedMenuIcon,
  Avatar,
  Box,
  CanAccess,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  SvgIcon,
  Tooltip,
  Typography,
} from 'components';
import { motion } from 'framer-motion';

import { MenuNavigationItem } from './MenuNavigationItem';

import {
  RouterLink,
  useMatch,
  useNavigate,
  useSelector,
  useTranslation,
} from 'third-party';

import { TOOLTIP_APPEAR_DELAY } from 'constants/common';
import {
  allOrdersUrl,
  contractorProjectsListUrl,
  contractorTeamListUrl,
  contractorsUrl,
  helpCenterEditorUrl,
  productsUrl,
  providerUsersListUrl,
  settingsUrl,
  termsAndConditionsEditorUrl,
  termsAndConditionsUrl,
  todayOrdersUrl,
  whatIsNewEditorUrl,
} from 'constants/url';

import { AppLeftPanel, useApp } from 'modules/app';
import { useStorage } from 'modules/storage';

import { appSelectors } from 'selectors';

import {
  DispatcherCanAccess,
  RegularUserCanAccess,
  SupportCanAccess,
} from 'components/CanAccess/CanAccess';

import { DialogsButton } from 'features/DialogsPanel/DialogsButton';
import { RequiredActionsButton } from 'features/RequiredActionsPanel/RequiredActionsButton';

import { useLogOutUser } from 'utils/auth';

const MenuIconStyle = css`
  color: ${props => props.theme.custom.palette.secondary800};
  width: 2.2rem;
  height: 2.2rem;
  margin-right: 0.4rem;
`;

const StyledSettingsOutlinedIcon = styled(SettingsOutlinedIcon)`
  ${MenuIconStyle}
`;

const StyledAccountCircleOutlinedIcon = styled(AccountCircleOutlinedIcon)`
  ${MenuIconStyle}
`;

const StyledLanguageOutlinedIcon = styled(LanguageOutlinedIcon)`
  ${MenuIconStyle}
`;

const StyledExitToAppOutlinedIcon = styled(ExitToAppOutlinedIcon)`
  ${MenuIconStyle}
`;

const StyledContactSupportIcon = styled(ContactSupportIcon)`
  ${MenuIconStyle}
`;

const StyledFiberNewIcon = styled(FiberNewIcon)`
  ${MenuIconStyle}
`;

const ProviderIcon = styled(SvgIcon)`
  ${MenuIconStyle}
`;

const StyledMenuItem = styled(MenuItem)<{
  $isSelected?: boolean;
  $isProviderItem?: boolean;
}>`
  text-transform: capitalize;
  font-weight: 700;
  font-size: 1.4rem;
  line-height: 2.1rem;
  padding-left: 1rem;
  padding-top: ${props => (props.$isProviderItem ? 0.8 : 0.4)}rem;
  padding-bottom: ${props => (props.$isProviderItem ? 0.8 : 0.4)}rem;
  color: ${props => props.theme.custom.palette.secondary800};

  &:hover {
    .MuiListItemText-root {
      color: ${props => props.theme.custom.palette.secondary800};
    }
    .MuiListItemText-root {
      color: ${props => props.theme.custom.palette.secondary800};
    }
    .MuiSvgIcon-root {
      color: ${props => props.theme.custom.palette.secondary800};
    }
  }
  ${props =>
    props.$isProviderItem &&
    css`
      border-radius: 0.4rem;
      margin: 0.4rem;
    `};

  ${props =>
    props.$isSelected
      ? 'background-color:' + props.theme.custom.palette.primary200
      : ''};

  ${props =>
    props.$isSelected
      ? 'color:' + props.theme.custom.palette.secondary900
      : ''};

  &:hover {
    background-color: ${props => props.theme.custom.palette.primary500};
    color: ${props => props.theme.custom.palette.secondary900};
    ${StyledSettingsOutlinedIcon}, ${StyledAccountCircleOutlinedIcon}, ${StyledExitToAppOutlinedIcon}, ${StyledContactSupportIcon}, ${StyledFiberNewIcon} {
      color: ${props => props.theme.custom.palette.secondary800};
    }
  }
`;

const MenuDivider = styled(Divider)`
  margin: 0 !important;
  border-color: ${props => props.theme.custom.palette.muted200};
`;

const NavigationWrapper = styled.div`
  display: flex;
  width: 70px;
  padding-top: 5px;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  background: ${props => props.theme.custom.palette.themeBackground};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.25);
  z-index: ${props => props.theme.custom.zIndex.mainNavigation};
`;

const StyledLogo = styled(SvgIcon)`
  margin: 1rem 1rem 1.5rem 1rem;
`;

const BottomButtonsWrapper = styled.div`
  margin-top: auto;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2rem;
`;

const LogoWrapper = styled.div`
  padding-top: 1.5rem;
  &:hover {
    cursor: pointer;
  }
`;

const StyledMenu = styled(Menu)`
  .MuiPopover-paper {
    margin-left: 5rem;
    min-width: 18rem;
    max-width: 40rem;
  }
  .MuiMenu-list {
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const StyledAvatarSection = styled.div`
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid ${props => props.theme.custom.palette.muted200};
  padding: 1.2rem;
  gap: 1.2rem;
`;

const UserInfoWrapper = styled.div`
  overflow: hidden;
  text-overflow: 'ellipsis';
  max-width: 25rem;
`;

const StyledName = styled(Typography)`
  padding-bottom: 3px;
`;

const VersionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${props => props.theme.custom.palette.muted500};
`;

const LegalAgreementLinkWrapper = styled(RouterLink)`
  font-size: 1.2rem;
  line-height: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.custom.palette.primary800};
  margin: 0.25rem 1.5rem 0.5rem 1rem;
  text-decoration: none;
  &:visited {
    color: ${props => props.theme.custom.palette.primary800};
  }
`;

const VersionText = styled(Typography)`
  font-size: 1.2rem;
  line-height: 1.5rem;
  margin: 0.25rem 1.5rem 0.5rem 1rem;
`;

const AvatarButton = styled(IconButton)`
  padding: 0;
` as typeof IconButton;

const StyledInfoButton = styled(IconButton)<{ active?: string }>`
  background-color: ${props =>
    props.active === 'true'
      ? props.theme.custom.palette.primary50
      : 'transparent'};
  width: 4rem;
  height: 4rem;
`;

const StyledInfoOutlinedIcon = styled(HelpIcon)`
  width: 3rem;
  color: ${props => props.theme.custom.palette.secondary900};
`;

const StyledLanguageSelectorWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const StyledLanguageMenu = styled(Menu)`
  .MuiPopover-paper {
    margin-left: 0.1rem;
    min-width: 18rem;
    max-width: 40rem;
  }
  .MuiMenu-list {
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [languageMenuAnchorEl, setLanguageMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const { updateUserSettingInStorage } = useStorage();

  const isMenuOpen = Boolean(languageMenuAnchorEl);

  const handleMenuClose = () => {
    setLanguageMenuAnchorEl(null);
  };

  const changeLanguage = (language: string) => {
    setLanguageMenuAnchorEl(null);

    updateUserSettingInStorage({
      selectedLanguage: language,
    });
  };

  const onLanguageButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageMenuAnchorEl(event.currentTarget);
  };

  return (
    <>
      <StyledMenuItem onClick={onLanguageButtonClick}>
        <StyledLanguageSelectorWrapper>
          <Box display="flex" flexDirection="row">
            <StyledLanguageOutlinedIcon />
            {LANGUAGE_NAMES[i18n.language] || i18n.language}
          </Box>
          <ChevronRightIcon />
        </StyledLanguageSelectorWrapper>
      </StyledMenuItem>
      <StyledLanguageMenu
        id="basic-menu"
        anchorEl={languageMenuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {Object.keys(i18n.options.resources).map(language => (
          <StyledMenuItem
            disabled={language === i18n.language}
            key={language}
            id={`lang-${language}`}
            onClick={changeLanguage.bind(null, language)}
          >
            {LANGUAGE_NAMES[language] || language}
          </StyledMenuItem>
        ))}
      </StyledLanguageMenu>
    </>
  );
};

const SupportPanelBottomButtons = () => {
  const { t } = useTranslation();

  const { updateUserSettingInStorage } = useStorage();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const logOutUser = useLogOutUser();
  const isMenuOpen = Boolean(menuAnchorEl);

  const { firstName, lastName, email } = useSelector(appSelectors.user);
  const userName = ` ${firstName} ${lastName}`;

  const onProfileButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const onSignOutClick = async () => {
    handleMenuClose();
    await logOutUser();
  };

  const handleRequiredPanelButtonClick = useCallback(() => {
    updateUserSettingInStorage({ openedLeftPanel: null });
    handleMenuClose();
  }, [updateUserSettingInStorage]);

  return (
    <BottomButtonsWrapper>
      <RequiredActionsButton onClick={handleRequiredPanelButtonClick} />
      <Tooltip enterDelay={TOOLTIP_APPEAR_DELAY} title={userName}>
        <AvatarButton
          id="user-actions"
          onClick={onProfileButtonClick}
          component={motion.button}
          whileHover="enlarge"
        >
          <Avatar firstName={firstName} lastName={lastName} />
        </AvatarButton>
      </Tooltip>
      <StyledMenu
        id="basic-menu"
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
      >
        <StyledAvatarSection>
          <Avatar firstName={firstName} lastName={lastName} />
          <UserInfoWrapper>
            <Tooltip title={userName} placement="right-end">
              <StyledName
                textTransform={'capitalize'}
                noWrap
                variant="subtitle2"
              >
                {userName}
              </StyledName>
            </Tooltip>
            <Tooltip title={email}>
              <StyledName noWrap variant="inherit" fontSize={13}>
                {email}
              </StyledName>
            </Tooltip>
          </UserInfoWrapper>
        </StyledAvatarSection>
        <StyledMenuItem id="user-logout" onClick={onSignOutClick}>
          <StyledExitToAppOutlinedIcon />
          {t('login.logout')}
        </StyledMenuItem>
        <MenuDivider />
        <VersionWrapper>
          <VersionText>
            {t('app.version')} {APPLICATION_VERSION}
          </VersionText>
        </VersionWrapper>
      </StyledMenu>
    </BottomButtonsWrapper>
  );
};

const PanelBottomButtons = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const { firstName, lastName, email } = useSelector(appSelectors.user);
  const userName = ` ${firstName} ${lastName}`;

  const logOutUser = useLogOutUser();

  const providers = useSelector(userProviders);
  const providerId = useSelector(appSelectors.providerId);

  const { setSelectedProvider } = useApp();
  const { updateUserSettingInStorage } = useStorage();
  const userType = useSelector(appSelectors.userType);

  const isHelpCenterPanelOpen = useSelector(appSelectors.isHelpCenterPanelOpen);
  const isMenuOpen = Boolean(menuAnchorEl);
  const onProfileButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };
  const onHelpButtonClicked = () => {
    updateUserSettingInStorage({
      openedLeftPanel: isHelpCenterPanelOpen ? null : AppLeftPanel.HelpCenter,
    });
  };

  const onSignOutClick = async () => {
    handleMenuClose();
    await logOutUser();
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const onProviderClick = (providerId: string) => {
    setSelectedProvider(providerId);
    updateUserSettingInStorage({ currentProviderId: providerId });
    navigate(allOrdersUrl());
    handleMenuClose();
  };

  const onUsersClick = () => {
    handleMenuClose();

    navigate(
      userType === APIUserType.Dispatcher || userType === APIUserType.Support
        ? providerUsersListUrl()
        : contractorTeamListUrl(),
    );
  };

  const onSettingsClick = () => {
    navigate(settingsUrl());
    handleMenuClose();
  };

  const handleRequiredPanelButtonClick = useCallback(() => {
    updateUserSettingInStorage({ openedLeftPanel: null });
    handleMenuClose();
  }, [updateUserSettingInStorage]);

  const handleDialogsPanelButtonClick = useCallback(() => {
    updateUserSettingInStorage({ openedLeftPanel: null });
    handleMenuClose();
  }, [updateUserSettingInStorage]);

  const unreadWhatsNews = useSelector(appSelectors.unreadWhatsNews);

  return (
    <BottomButtonsWrapper>
      <DialogsButton onClick={handleDialogsPanelButtonClick} />
      <RequiredActionsButton onClick={handleRequiredPanelButtonClick} />
      <RegularUserCanAccess>
        <StyledInfoButton
          id="help-center"
          active={String(isHelpCenterPanelOpen)}
          onClick={onHelpButtonClicked}
          // @ts-ignore
          component={motion.button}
          whileHover="enlarge"
        >
          <AnimatedMenuIcon
            unread={unreadWhatsNews}
            iconComponent={<StyledInfoOutlinedIcon />}
          />
        </StyledInfoButton>
      </RegularUserCanAccess>
      <Tooltip enterDelay={TOOLTIP_APPEAR_DELAY} title={userName}>
        <AvatarButton
          id="user-actions"
          onClick={onProfileButtonClick}
          component={motion.button}
          whileHover="enlarge"
        >
          <Avatar firstName={firstName} lastName={lastName} />
        </AvatarButton>
      </Tooltip>
      <StyledMenu
        id="basic-menu"
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
      >
        <StyledAvatarSection>
          <Avatar firstName={firstName} lastName={lastName} />
          <UserInfoWrapper>
            <Tooltip title={userName} placement="right-end">
              <StyledName
                textTransform={'capitalize'}
                noWrap
                variant="subtitle2"
              >
                {userName}
              </StyledName>
            </Tooltip>
            <Tooltip title={email}>
              <StyledName noWrap variant="inherit" fontSize={13}>
                {email}
              </StyledName>
            </Tooltip>
          </UserInfoWrapper>
        </StyledAvatarSection>
        {providers && providers?.length > 1 && (
          <CanAccess
            allowedUserType={[APIUserType.Dispatcher, APIUserType.Contractor]}
          >
            {providers?.map(it => (
              <StyledMenuItem
                $isProviderItem
                key={it.id}
                id={`provider-${it.id}`}
                $isSelected={it.id === providerId}
                onClick={() => onProviderClick(it.id as string)}
              >
                <ProviderIcon icon="provider-icon" width="2rem" height="2rem" />
                {it.providerName}
              </StyledMenuItem>
            ))}
            <MenuDivider />
          </CanAccess>
        )}
        <DispatcherCanAccess>
          <CanAccess allowedFor={APIAccessLevel.Admin}>
            <StyledMenuItem id="app-setting" onClick={onSettingsClick}>
              <StyledSettingsOutlinedIcon />
              {t('appbar.menu.setting')}
            </StyledMenuItem>
          </CanAccess>
        </DispatcherCanAccess>
        <CanAccess allowedFor={APIAccessLevel.Admin}>
          <StyledMenuItem id="app-users" onClick={onUsersClick}>
            <StyledAccountCircleOutlinedIcon />
            {t('appbar.menu.users')}
          </StyledMenuItem>
        </CanAccess>
        <LanguageSelector />
        <MenuDivider />
        <StyledMenuItem id="app-logout" onClick={onSignOutClick}>
          <StyledExitToAppOutlinedIcon />
          {t('login.logout')}
        </StyledMenuItem>
        <MenuDivider />
        <VersionWrapper id="app-version">
          <LegalAgreementLinkWrapper to={termsAndConditionsUrl()}>
            {t('app.menu.legalAgreements')}
          </LegalAgreementLinkWrapper>
          <VersionText>
            {t('app.version')} {APPLICATION_VERSION}
          </VersionText>
        </VersionWrapper>
      </StyledMenu>
    </BottomButtonsWrapper>
  );
};

export const MainNavigationContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      <SupportCanAccess>
        <MenuNavigationItem
          id="nav-providers"
          caption={t('appDrawer.menu.providers')}
          icon="nav-customer"
          isActive={!!useMatch([contractorsUrl(), '*'].join('/'))}
          onClick={() => navigate(contractorsUrl())}
        />
        <MenuNavigationItem
          id="nav-helpCenter"
          caption={t('appDrawer.menu.helpCenter')}
          icon={HelpCenterIcon}
          isActive={!!useMatch([helpCenterEditorUrl(), '*'].join('/'))}
          onClick={() =>
            navigate(
              helpCenterEditorUrl(DocumentsSections.HelpCenterWebDispatcher),
            )
          }
        />
        <MenuNavigationItem
          id="nav-whatsNew"
          caption={t('appDrawer.menu.whatsNew')}
          icon={FiberNewIcon}
          isActive={!!useMatch([whatIsNewEditorUrl(), '*'].join('/'))}
          onClick={() =>
            navigate(
              whatIsNewEditorUrl(DocumentsSections.WhatIsNewWebDispatcher),
            )
          }
        />
        <MenuNavigationItem
          id="nav-termsAndConditions"
          caption={t('appDrawer.menu.termsAndConditions')}
          icon={Gavel}
          isActive={!!useMatch([termsAndConditionsEditorUrl(), '*'].join('/'))}
          onClick={() =>
            navigate(
              termsAndConditionsEditorUrl(
                DocumentsSections.TermsAndConditionsWebDispatcher,
              ),
            )
          }
        />
        <SupportPanelBottomButtons />
      </SupportCanAccess>
      <RegularUserCanAccess>
        <DispatcherCanAccess>
          <MenuNavigationItem
            id="nav-dashboard"
            icon="nav-dashboard"
            caption={t('appDrawer.menu.dashBoard')}
            isActive={!!useMatch([todayOrdersUrl(), '*'].join('/'))}
            onClick={() => navigate(todayOrdersUrl())}
          />
        </DispatcherCanAccess>
        <MenuNavigationItem
          id="nav-orders"
          icon="nav-truck"
          caption={t('appDrawer.menu.orders')}
          isActive={!!useMatch([allOrdersUrl(), '*'].join('/'))}
          onClick={() => navigate(allOrdersUrl())}
        />
        <DispatcherCanAccess>
          <MenuNavigationItem
            id="nav-customers"
            icon="nav-customer"
            caption={t('appDrawer.menu.customer')}
            isActive={!!useMatch([contractorsUrl(), '*'].join('/'))}
            onClick={() => navigate(contractorsUrl())}
          />
        </DispatcherCanAccess>

        <CanAccess allowedUserType={APIUserType.Contractor}>
          <MenuNavigationItem
            id="nav-projects"
            icon="nav-projects"
            caption={t('appDrawer.menu.projects')}
            isActive={!!useMatch([contractorProjectsListUrl(), '*'].join('/'))}
            onClick={() => navigate(contractorProjectsListUrl())}
          />
        </CanAccess>
        <DispatcherCanAccess>
          <MenuNavigationItem
            id="nav-products"
            icon="nav-products"
            caption={t('appDrawer.menu.products')}
            isActive={!!useMatch([productsUrl(), '*'].join('/'))}
            onClick={() => navigate(productsUrl())}
          />
        </DispatcherCanAccess>
        <PanelBottomButtons />
      </RegularUserCanAccess>
    </>
  );
};

export const MainNavigation = () => {
  const navigate = useNavigate();
  return (
    <NavigationWrapper>
      <LogoWrapper onClick={() => navigate(allOrdersUrl())}>
        <StyledLogo height="5rem" width="5rem" icon="logo-small" />
      </LogoWrapper>
      <MainNavigationContent />
    </NavigationWrapper>
  );
};
